// M03 Passwort vergessen / zurücksetzen. Die Antwort verrät nie, ob es ein Konto gibt.
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ApiError, api } from '../../api/client';
import { serverMessage } from '../../api/account';
import { Field } from '../../components/ui';
import { AuthLayout } from '../AuthLayout';
import { PasswordFields, passwordOk } from './PasswordFields';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post('/auth/password/forgot', { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 429 ? t('errors.rate_limited') : t('errors.network'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout>
      <h1>{t('account.forgot.title')}</h1>
      {sent ? (
        <div className="col" style={{ marginTop: 20 }}>
          <div className="hint" role="status">{t('account.forgot.sent', { email: email.trim() })}</div>
          <div className="muted small">{t('account.forgot.noMail')}</div>
          <Link className="btn primary" to="/login">{t('account.forgot.back')}</Link>
        </div>
      ) : (
        <>
          <div className="muted">{t('account.forgot.text')}</div>
          <form onSubmit={submit} noValidate>
            <Field label={t('login.email')}>
              <input type="email" autoComplete="username" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firma.ch" />
            </Field>
            <div className="error" role="alert">{error}</div>
            <button className="btn primary" disabled={busy || !email.includes('@')}>{t('account.forgot.submit')}</button>
            <Link to="/login" className="small">{t('account.forgot.back')}</Link>
          </form>
        </>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const loc = useLocation();
  // Token steht im Fragment (#token=…), damit er nicht in Server-Logs oder Referrern landet
  const token = new URLSearchParams(loc.hash.replace(/^#/, '')).get('token') ?? new URLSearchParams(loc.search).get('token') ?? '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post('/auth/password/reset', { token, newPassword: pw });
      setDone(true);
      window.history.replaceState(null, '', '/password/reset');
    } catch (err) {
      setError(serverMessage(err) ?? t('errors.network'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout>
      <h1>{t('account.reset.title')}</h1>
      {done ? (
        <div className="col" style={{ marginTop: 20 }}>
          <div className="hint" role="status">{t('account.reset.done')}</div>
          <Link className="btn primary" to="/login">{t('login.submit')}</Link>
        </div>
      ) : !token ? (
        <div className="col" style={{ marginTop: 20 }}>
          <div className="hint warn">{t('account.reset.noToken')}</div>
          <Link className="btn" to="/password/forgot">{t('account.forgot.title')}</Link>
        </div>
      ) : (
        <>
          <div className="muted">{t('account.reset.text')}</div>
          <form onSubmit={submit} noValidate>
            <PasswordFields value={pw} repeat={pw2} onChange={setPw} onRepeat={setPw2} />
            <div className="error" role="alert">{error}</div>
            <button className="btn primary" disabled={busy || !passwordOk(pw, pw2)}>{t('account.reset.submit')}</button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
